import { useState } from "react";
import { api } from "../lib/api";
import { getAttribution } from "../lib/attribution";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar as CalendarIcon, Check, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import Reveal from "./Reveal";

export default function ClaimForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    property_address: "", county: "", state: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        source: "claim_estimate",
        foreclosure_date: date ? format(date, "yyyy-MM-dd") : null,
        ...getAttribution(),
      });
      setDone(true);
      toast.success("Got it! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="claim" className="py-24 lg:py-32 bg-[#F7F5F0]" data-testid="claim-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#1E3A8A] grid place-items-center mx-auto text-white">
            <Check className="w-8 h-8" />
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#0F172A] mt-6">Thank you, {form.name.split(" ")[0]}.</h2>
          <p className="mt-4 text-[#475569]">Your claim request has been received. A recovery specialist will reach out within 24 business hours to walk through your case — no cost, no obligation.</p>
          <Button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", property_address: "", county: "", state: "", message: "" }); setDate(null); }} variant="outline" className="mt-8 border-[#E2DDD3] rounded-full" data-testid="submit-another-claim">
            Submit another claim
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="claim" className="py-20 sm:py-24 lg:py-32 bg-[#F7F5F0]" data-testid="claim-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 lg:gap-12">
        <Reveal className="lg:col-span-5">
          <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">Free Claim Estimate</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
            Find out — for free — if surplus funds are waiting for you.
          </h2>
          <p className="mt-5 text-[#475569] leading-relaxed">
            Fill out the form and a recovery specialist will personally review your case within 24 business hours. There's no upfront cost — we only get paid when we successfully recover funds for you.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-[#0F172A]">
            <CheckItem>Confidential & free case review</CheckItem>
            <CheckItem>No obligation, no upfront fees</CheckItem>
            <CheckItem>Typical response within 24 hours</CheckItem>
          </ul>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E2DDD3] shadow-sm p-6 sm:p-8 lg:p-10" data-testid="claim-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *">
              <Input value={form.name} onChange={set("name")} className="bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="claim-name" required />
            </Field>
            <Field label="Phone *">
              <Input value={form.phone} onChange={set("phone")} type="tel" className="bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="claim-phone" required />
            </Field>
            <Field label="Email *" className="sm:col-span-2">
              <Input value={form.email} onChange={set("email")} type="email" className="bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="claim-email" required />
            </Field>
            <Field label="Property Address" className="sm:col-span-2">
              <Input value={form.property_address} onChange={set("property_address")} className="bg-[#F7F5F0] border-[#E2DDD3] h-11" placeholder="123 Main Street" data-testid="claim-address" />
            </Field>
            <Field label="County">
              <Input value={form.county} onChange={set("county")} className="bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="claim-county" />
            </Field>
            <Field label="State">
              <Input value={form.state} onChange={set("state")} className="bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="claim-state" />
            </Field>
            <Field label="Foreclosure Date" className="sm:col-span-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center justify-between w-full h-11 px-3 rounded-md bg-[#F7F5F0] border border-[#E2DDD3] text-left text-sm text-[#0F172A] hover:bg-[#EFEAE0]" data-testid="claim-foreclosure-date">
                    {date ? format(date, "PPP") : <span className="text-[#94A3B8]">Pick approximate date (optional)</span>}
                    <CalendarIcon className="w-4 h-4 text-[#1E3A8A]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Anything else we should know?" className="sm:col-span-2">
              <Textarea value={form.message} onChange={set("message")} className="bg-[#F7F5F0] border-[#E2DDD3] min-h-24" placeholder="Optional details about your situation…" data-testid="claim-message" />
            </Field>
          </div>

          <Button type="submit" disabled={submitting} className="mt-8 w-full bg-[#FF008F] hover:bg-[#D60079] text-white rounded-full h-12 text-lg font-bold transition-all duration-300 hover:-translate-y-0.5" data-testid="claim-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : "Get My Free Claim Estimate"}
          </Button>
          <p className="text-xs text-[#64748B] mt-4 text-center">By submitting, you agree to be contacted by American Asset Recovery Agency. We never sell your information.</p>
        </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CheckItem({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1E3A8A] grid place-items-center shrink-0"><Check className="w-3 h-3 text-white" /></span>
      <span>{children}</span>
    </li>
  );
}
